
import React from 'react';
import { Filter, Download, AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from '@/hooks/use-mobile';

// Types
export interface NonCompliantGroup {
  id: number;
  name: string;
  owner: string;
  violations: string[];
  severity: string;
  created: string;
}

interface NonCompliantGroupsProps {
  groups: NonCompliantGroup[];
  loading?: boolean;
}

export const getSeverityBadge = (severity: string) => {
  switch(severity) {
    case 'Critical':
      return <Badge className="bg-red-600 hover:bg-red-700">Critical</Badge>;
    case 'High':
      return <Badge className="bg-red-500 hover:bg-red-600">High</Badge>;
    case 'Medium':
      return <Badge className="bg-amber-500 hover:bg-amber-600">Medium</Badge>;
    case 'Low':
      return <Badge className="bg-blue-500 hover:bg-blue-600">Low</Badge>;
    default:
      return <Badge>{severity}</Badge>;
  }
};

const NonCompliantGroups: React.FC<NonCompliantGroupsProps> = ({ groups, loading = false }) => {
  const isMobile = useIsMobile();

  if (loading) {
    return (
      <>
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <Skeleton className="h-10 w-full md:w-64" />
            <div className="flex gap-3">
              <Skeleton className="h-10 w-1/2 md:w-36" />
              <Skeleton className="h-10 w-1/2 md:w-36" />
            </div>
          </div>
          <Skeleton className="h-10 w-full md:w-36 mt-3 md:mt-0" />
        </div>
        
        <Card>
          <div className="p-6">
            <div className="space-y-4">
              {Array(5).fill(0).map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-12 flex-1" />
                </div>
              ))}
            </div>
          </div>
        </Card>
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative w-full md:w-64">
            <Input 
              placeholder="Search teams..."
              className="pl-8 w-full"
            />
            <Filter className="h-4 w-4 absolute left-2.5 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          </div>
          <div className="flex gap-3">
            <Select defaultValue="all-severity">
              <SelectTrigger className="w-full md:w-36">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-severity">All Severity</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Select defaultValue="all-violations">
              <SelectTrigger className="w-full md:w-36">
                <SelectValue placeholder="Violation Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-violations">All Violations</SelectItem>
                <SelectItem value="external-sharing">External Sharing</SelectItem>
                <SelectItem value="guest-access">Guest Access</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="naming-convention">Naming Convention</SelectItem>
                <SelectItem value="sensitivity-labels">Sensitivity Labels</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button variant="outline" className="mt-3 md:mt-0 w-full md:w-auto">
          <Download className="h-4 w-4 mr-2" />
          Export List
        </Button>
      </div>
      
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[180px]">Team/Group Name</TableHead>
                <TableHead className="min-w-[120px]">Owner</TableHead>
                <TableHead className="min-w-[180px]">Policy Violations</TableHead>
                <TableHead className="min-w-[100px]">Severity</TableHead>
                <TableHead className="min-w-[120px]">Created Date</TableHead>
                <TableHead className="text-right min-w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.length > 0 ? (
                groups.map((group) => (
                  <TableRow key={group.id}>
                    <TableCell className="font-medium">
                      {group.name}
                    </TableCell>
                    <TableCell>{group.owner}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {group.violations.map((violation, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {violation}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>{getSeverityBadge(group.severity)}</TableCell>
                    <TableCell>{group.created}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" className="h-8 whitespace-nowrap">Remediate</Button>
                        <Button variant="ghost" size="sm" className="h-8 whitespace-nowrap hidden md:inline-flex">Snooze</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="h-32 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <AlertTriangle className="h-8 w-8 text-amber-500 mb-2" />
                      <h3 className="font-medium">No non-compliant teams</h3>
                      <p className="text-sm text-muted-foreground">All teams comply with governance policies</p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </>
  );
};

export default NonCompliantGroups;
